import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { MagnifyingGlass, BookOpen, Clock, Plus, Archive, ArrowCounterClockwise } from "@phosphor-icons/react";

const empty = { name: "", language: "English", level: "A1", price: 0, duration_weeks: 8, description: "", syllabus: "" };
const LEVELS = ["A1", "A2", "B1", "B2", "C1", "C2", "Novice", "Beginner", "Intermediate", "Advanced"];
const LANGS = ["English", "Spanish", "French", "German", "Italian", "Mandarin", "Japanese", "Russian", "Turkish", "Arabic", "Portuguese"];

export function CourseForm({ initial, onSave, onClose }) {
  const { t } = useTranslation();
  const isEdit = !!initial?.id;
  const [f, setF] = useState(() => (initial ? { ...empty, ...initial } : empty));
  const [saving, setSaving] = useState(false);
  const setV = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });

  const submit = async () => {
    if (!f.name?.trim()) return toast.error(t("courses.form.nameRequired"));
    setSaving(true);
    try {
      const payload = {
        ...f,
        name: f.name.trim(),
        price: Number(f.price) || 0,
        duration_weeks: Number(f.duration_weeks) || 0,
      };
      const { data } = isEdit
        ? await api.put(`/courses/${initial.id}`, payload)
        : await api.post("/courses", payload);
      toast.success(isEdit ? t("courses.toasts.updated") : t("courses.toasts.created"));
      onSave(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.name")}</Label>
        <Input value={f.name || ""} onChange={setV("name")} className="mt-1.5 h-10"
          placeholder={t("courses.form.namePh")} data-testid="course-field-name" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.language")}</Label>
        <Select value={f.language} onValueChange={(v) => setF({ ...f, language: v })}>
          <SelectTrigger className="mt-1.5 h-10" data-testid="course-field-language"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LANGS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.level")}</Label>
        <Select value={f.level} onValueChange={(v) => setF({ ...f, level: v })}>
          <SelectTrigger className="mt-1.5 h-10" data-testid="course-field-level"><SelectValue /></SelectTrigger>
          <SelectContent>
            {LEVELS.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.price")}</Label>
        <Input type="number" value={f.price ?? ""} onChange={setV("price")} className="mt-1.5 h-10"
          data-testid="course-field-price" />
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.durationWeeks")}</Label>
        <Input type="number" value={f.duration_weeks ?? ""} onChange={setV("duration_weeks")}
          className="mt-1.5 h-10" data-testid="course-field-duration" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.description")}</Label>
        <Textarea value={f.description || ""} onChange={setV("description")} rows={3} className="mt-1.5"
          data-testid="course-field-description" />
      </div>
      <div className="md:col-span-2">
        <Label className="text-xs uppercase tracking-wider font-semibold">{t("courses.form.syllabus")}</Label>
        <Textarea value={f.syllabus || ""} onChange={setV("syllabus")} rows={5} className="mt-1.5"
          placeholder={t("courses.form.syllabusPh")} data-testid="course-field-syllabus" />
      </div>
      <DialogFooter className="md:col-span-2">
        <Button variant="outline" onClick={onClose} data-testid="course-form-cancel">{t("common.cancel")}</Button>
        <Button onClick={submit} disabled={saving} data-testid="course-form-save">
          {saving ? t("common.saving") : (isEdit ? t("common.saveChanges") : t("courses.createBtn"))}
        </Button>
      </DialogFooter>
    </div>
  );
}

export default function Courses() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [lang, setLang] = useState("");
  const [open, setOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const nav = useNavigate();
  const canManage = ["super_admin", "director", "branch_manager"].includes(user?.role);

  const load = () => api.get("/courses", { params: { archived: showArchived } }).then(({ data }) => setRows(data));
  useEffect(() => { load(); }, [showArchived]);

  const archive = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/courses/${id}/archive`); toast.success(t("courses.toasts.archived")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };
  const restore = async (id, e) => {
    e.stopPropagation();
    try { await api.post(`/courses/${id}/restore`); toast.success(t("courses.toasts.restored")); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  const languages = Array.from(new Set(rows.map((r) => r.language))).sort();
  const filtered = rows.filter((r) => {
    if (q && !r.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (lang && r.language !== lang) return false;
    return true;
  });

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{t("nav.courses")}</div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "Manrope" }}>{t("courses.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("courses.subtitle")}</p>
        </div>
        {canManage && (
          <Dialog open={open} onOpenChange={setOpen}>
            <Button data-testid="add-course-btn" onClick={() => setOpen(true)} className="rounded-full gap-2">
              <Plus size={16} weight="bold" /> {t("courses.createBtn")}
            </Button>
            <DialogContent className="max-w-2xl">
              <DialogHeader><DialogTitle style={{ fontFamily: "Manrope" }}>{t("courses.newCourse")}</DialogTitle></DialogHeader>
              <CourseForm onSave={load} onClose={() => setOpen(false)} />
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="courses-search" placeholder={t("common.search")} value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-10" />
        </div>
        <div className="flex gap-2 flex-wrap items-center">
          <button onClick={() => setLang("")} data-testid="filter-lang-all"
            className={`px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${!lang ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
            {t("common.all")}
          </button>
          {languages.map((l) => (
            <button key={l} onClick={() => setLang(l)} data-testid={`filter-lang-${l.toLowerCase()}`}
              className={`px-3 py-2 rounded-full text-xs font-semibold border transition-colors ${lang === l ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}>
              {l}
            </button>
          ))}
          {canManage && (
            <button
              data-testid="show-archived-courses"
              onClick={() => setShowArchived((v) => !v)}
              className={`h-9 px-3 rounded-full text-xs font-semibold border transition-colors flex items-center gap-2 ${showArchived ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`}
            >
              <Archive size={12} weight={showArchived ? "fill" : "regular"} />
              {showArchived ? t("common.hideArchived") : t("common.showArchived")}
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filtered.map((c) => (
          <Card key={c.id} data-testid={`course-card-${c.id}`}
            onClick={() => nav(`/courses/${c.id}`)}
            className="border-border/60 card-hover cursor-pointer overflow-hidden">
            <div className="h-24 bg-gradient-to-br from-primary via-primary/90 to-destructive/70 relative">
              <div className="absolute top-3 left-3 flex gap-1.5">
                <Badge className="bg-white/95 text-primary font-mono">{c.level}</Badge>
                <Badge variant="secondary">{c.language}</Badge>
              </div>
              <BookOpen size={40} weight="duotone" className="absolute right-4 bottom-4 text-primary-foreground opacity-30" />
            </div>
            <CardContent className="p-5">
              <h3 className="font-semibold text-lg" style={{ fontFamily: "Manrope" }}>{c.name}</h3>
              <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1.5">
                <span className="flex items-center gap-1"><Clock size={12} /> {c.duration_weeks} weeks</span>
                <span>·</span>
                <span>{(c.lesson_plans || []).length} lessons</span>
                {c.archived && <Badge variant="destructive" className="text-[10px]">{t("common.archived")}</Badge>}
              </div>
              <div className="mt-4 pt-4 border-t border-border flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</span>
                <span className="text-xl font-bold font-mono">${c.price}</span>
              </div>
              {canManage && (
                <div className="mt-3 pt-3 border-t border-border flex justify-end">
                  {c.archived ? (
                    <Button size="sm" variant="outline" onClick={(e) => restore(c.id, e)}
                      data-testid={`restore-course-${c.id}`} className="gap-1.5 h-8 text-xs">
                      <ArrowCounterClockwise size={12} weight="bold" /> {t("common.restore")}
                    </Button>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={(e) => archive(c.id, e)}
                      data-testid={`archive-course-${c.id}`} className="gap-1.5 h-8 text-xs text-muted-foreground hover:text-destructive">
                      <Archive size={12} weight="bold" /> {t("common.archive")}
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
